/**
 * The two write doors.
 *
 * Decision 0007 says the runtime must refuse a write a spec did not declare.
 * "Refuse" is the weaker of the two things it could mean, and this file picks
 * the stronger one: an undeclared mutation has **no expressible form**.
 *
 * The type system carries most of that and cannot carry all of it: an authority
 * is an ordinary object at runtime, so `write`, `edit`, `undo` and both manifest
 * doors check the brand below before they do anything else. Decision 0010 puts
 * third-party module code in the same realm on purpose, and TypeScript is not
 * there when it runs.
 *
 * A `Grant` is minted only by validating the rendered spec's bindings, and only
 * a `read-write` binding produces one. A `read` slot is not in `writable`, so
 * there is no grant to name it with; and `Instruction` welds each grant type to
 * its payload, so a `toggle` grant cannot appear in a set-value instruction —
 * a compile error, not a runtime check.
 *
 * Creation and schema change are a different door entirely (`EditorAuthority`),
 * because "the layout let me edit this field" and "I may add a property to this
 * collection" are different permissions and collapsing them loses the first.
 */

import type { Mutation, RelationMutation, Role, TBinding } from '@morph/spec';
import { WRITABLE, EDITOR, GRANT, RELATION_TARGET } from './brand.js';
import type { ManifestRef, ObjectId, PropertyKeyId, Revision } from './ids.js';
import type { Value } from './value.js';

export type RefusalCode =
  | 'read-only-binding' | 'undeclared-mutation' | 'mutation-role-mismatch'
  | 'ambiguous-role' | 'unknown-slot' | 'unknown-object' | 'not-editable'
  | 'order-not-semantic' | 'kind-mismatch' | 'stale' | 'closed'
  /** The store could not be brought back to a consistent state. Its own code
   *  rather than `stale`, because decision 0027 turns `stale` into an
   *  instruction — the engine has refreshed, plan it again — and a caller told
   *  to retry against state that never arrived would loop for ever. */
  | 'backend'
  /** Two codes beyond the original design. Folding them into `not-editable`
   *  would have made the two most common setup mistakes — an unvalidatable spec
   *  and a manifest ref from another store — indistinguishable from a
   *  permission failure, which is the one refusal a caller must not retry. */
  | 'invalid-spec' | 'unknown-manifest'
  /** Decision 0016's relation refusals. `unknown-target` is its own code
   *  because a stale handle — the occurrence was detached by somebody else
   *  between render and gesture — is a retry-with-fresh-state, not a
   *  permission failure and not a typo. */
  | 'unknown-target';

export interface Refusal {
  readonly ok: false;
  readonly code: RefusalCode;
  readonly message: string;
}

export const refuse = (code: RefusalCode, message: string): Refusal =>
  ({ ok: false, code, message });

export const isRefusal = (v: unknown): v is Refusal =>
  typeof v === 'object' && v !== null && (v as { ok?: unknown }).ok === false;

export interface Actor { readonly id: string }

export interface WritePolicy {
  canEdit(actor: Actor, id: ObjectId): Promise<boolean>;
  canCreate(actor: Actor): Promise<boolean>;
}

/** The only inhabitants are bindings that passed `authorize`. There is no other
 *  constructor, so a `read` binding has no value of this type anywhere. */
export type WriteBinding<M extends Mutation = Mutation> = TBinding & {
  readonly direction: 'read-write';
  readonly mutation: M;
  readonly [WRITABLE]: true;
};

export interface BindingAuthority {
  readonly [WRITABLE]: true;
  readonly manifest: ManifestRef;
  readonly specHash: string;
  /** Only the read-write slots. A read slot is not in this map, so naming it is
   *  refused — the mutation has no form to be expressed in. */
  readonly writable: ReadonlyMap<string, WriteBinding>;
  /** Every slot the spec declared read-only. Not needed to *enforce* anything —
   *  absence from `writable` already does that — but a caller told
   *  `unknown-slot` when the slot is right there in the layout will go looking
   *  for a typo instead of for the missing `direction: 'read-write'`. */
  readonly readOnly: ReadonlySet<string>;
  /** Slots that declared a mutation the store will not grant, with the reason.
   *  Kept per slot so the refusal names the real cause rather than collapsing
   *  to "not writable". */
  readonly refused: ReadonlyMap<string, Refusal>;
  /**
   * Node slots that declared relation mutations, by node id.
   *
   * A separate map because a node slot is NOT a binding — decision 0016 gives
   * it `mutations: ('link' | 'unlink')[]` precisely because attach and detach
   * are two authorities and a `Binding.mutation` holds one. So one entry can
   * mint more than one grant, and the caller picks by gesture rather than the
   * store picking for it.
   */
  readonly relationSlots: ReadonlyMap<string, RelationSlot>;
  /** Present only when `authorize` was given one. Grants are then checked
   *  against `WritePolicy.canEdit` per object, which is the second half of
   *  0007 — a spec may only write what it declared, *and* only to objects the
   *  user can already edit. */
  readonly actor?: Actor;
}

export interface RelationSlot {
  readonly role: Role;
  readonly mutations: readonly RelationMutation[];
}

/**
 * An opaque, store-minted reference to ONE rendered relation occurrence.
 *
 * Decision 0016: *"The target travels through the mutation boundary as an
 * opaque, store-minted `RelationTargetHandle` that resolves to a target object
 * plus an optional target anchor… neither exposes an internal raw ID, and
 * neither may be constructed from typed text."*
 *
 * It holds a token, not the object's id, and the store keeps the mapping — so
 * a caller cannot invent a target, and cannot turn a handle back into an
 * address to use somewhere the grant does not reach.
 *
 * **Occurrence-specific.** Two links to one object through two anchors are two
 * members of the relation set, so they are two handles; detach consumes the one
 * attached to the occurrence the person actually clicked.
 */
export interface RelationTargetHandle {
  readonly [RELATION_TARGET]: true;
  readonly token: string;
}

export interface EditorAuthority {
  readonly [EDITOR]: true;
  readonly actor: Actor;
}

export interface Grant<M extends Mutation> {
  readonly [GRANT]: true;
  readonly mutation: M;
  readonly object: ObjectId;
  readonly role: Role;
  readonly key: PropertyKeyId;      // resolved inside the store; callers never name a key
  readonly revision: Revision;
}

export type AnyGrant = { [M in Mutation]: Grant<M> }[Mutation];

/**
 * The payload meaning *this slot holds nothing*, as distinct from any value.
 *
 * Task 0096, and @Codex's ruling on it: **empty is a property state, not a
 * schema mutation.** The store has always distinguished three slot states and
 * its editor door has always named two operations — `clear` produces `empty`,
 * `drop` produces `absent` — but a binding instruction could not say the first,
 * because every scalar arm below was welded to a payload type that only values
 * inhabit. `set-enum`'s was `string`, and no string means "no value".
 *
 * So the demo bridge refused to clear an enum, citing a rule about making slots
 * ABSENT — which clearing does not do. The refusal was downstream of THIS TYPE
 * rather than of a policy, and every enum in the product was a one-way door
 * because of it.
 *
 * A distinct sentinel rather than a `Value` variant on purpose: `Value` is what
 * a slot HOLDS, it is pooled and compared and rendered everywhere, and adding
 * an "empty value" to it would make absence a thing you could store. The
 * authority to send this stays exactly the binding grant that could already
 * write the slot — clearing is the value-state of an authorized binding, not a
 * second capability, which is why the closed mutation set does not grow.
 */
export const CLEAR = Symbol('clear');
export type Clear = typeof CLEAR;

/** Grant and payload are welded together per mutation, so a toggle grant cannot
 *  appear in a set-value instruction — a compile error, not a runtime check.
 *
 *  The scalar arms additionally admit `CLEAR`; the relation and positional arms
 *  do not, because neither `unlink` nor a drag has a slot state to return to. */
export type Instruction =
  | { readonly mutation: 'set-value';  readonly grant: Grant<'set-value'>;
      readonly value: Value | Clear }
  | { readonly mutation: 'set-enum';   readonly grant: Grant<'set-enum'>;
      readonly value: string | Clear }
  | { readonly mutation: 'set-date';   readonly grant: Grant<'set-date'>;
      readonly value: string | Clear }
  | { readonly mutation: 'toggle';     readonly grant: Grant<'toggle'> }
  | { readonly mutation: 'reorder-within';
      readonly grant: Grant<'reorder-within'>; readonly after: ObjectId | null }
  | { readonly mutation: 'move-between-groups';
      readonly grant: Grant<'move-between-groups'>;
      readonly group: string; readonly after: ObjectId | null }
  // The relation arms. Their payload is a handle rather than a `Value`, which
  // is the whole reason `set-value` cannot stand in for them: scalar
  // replacement can model a singular pointer and cannot model set membership.
  | { readonly mutation: 'link';   readonly grant: Grant<'link'>;
      readonly target: RelationTargetHandle }
  | { readonly mutation: 'unlink'; readonly grant: Grant<'unlink'>;
      readonly target: RelationTargetHandle };
